import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { ArrowRight } from 'lucide-react'
import { useDictionary } from '@/lib/i18n'
import ButtonLink from '@/components/generals/button-link'

import { BIKE_IMAGES, FRAME_SIZES, PRICE } from '@/constants/bike'

const HeroSection: FC = (): ReactNode => {
    const t = useDictionary()
    const image = BIKE_IMAGES.profile

    return (
        <section id='hero' aria-labelledby='hero-title' className='relative isolate overflow-hidden pt-16'>
            <div aria-hidden='true' className='pointer-events-none absolute inset-0 -z-10'>
                <div className='absolute left-1/2 top-[60%] h-[62vmin] w-[120vmin] -translate-x-1/2 -translate-y-1/2 rounded-full bg-race/35 blur-[120px] motion-safe:animate-glow' />
                <div className='absolute inset-x-0 top-0 h-48 bg-gradient-to-b from-black/60 to-transparent' />
                <div className='absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-background' />
                <div className='noise absolute inset-0 opacity-[0.05]' />
            </div>

            <p
                aria-hidden='true'
                className='type-outline pointer-events-none absolute inset-x-0 top-[36%] -z-10 select-none text-center font-heading text-[25vw] font-bold uppercase leading-none tracking-tighter sm:top-[30%]'
            >
                Legends
            </p>

            <div className='mx-auto flex min-h-[calc(100svh-4rem)] max-w-7xl flex-col px-5 pb-8 sm:px-8'>
                <div className='mx-auto max-w-4xl pt-10 text-center animate-in fade-in slide-in-from-bottom-4 duration-700 motion-reduce:animate-none sm:pt-14'>
                    <p className='inline-flex max-w-full items-center gap-2.5 rounded-2xl border border-border bg-white/[0.04] px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-vapor text-balance sm:rounded-full sm:text-[11px] sm:tracking-[0.2em]'>
                        <span aria-hidden='true' className='size-1.5 shrink-0 rounded-full bg-race-bright' />
                        {t.hero.eyebrow}
                    </p>

                    <h1 id='hero-title' className='mt-7'>
                        <span lang='en' className='block font-heading text-base font-semibold uppercase tracking-[0.32em] text-vapor sm:text-lg'>
                            {t.hero.titleSmall}
                        </span>

                        <span lang='en' className='display-heading mt-3 block font-heading text-[length:clamp(3.25rem,10.5vw,8.25rem)] font-bold uppercase leading-[0.86] tracking-tight text-balance text-foreground'>
                            {t.hero.titleLarge}
                        </span>
                    </h1>

                    <p className='mx-auto mt-6 max-w-xl text-base leading-relaxed text-muted-foreground text-pretty sm:text-lg'>
                        {t.hero.lead}
                    </p>
                </div>

                <div className='relative flex flex-1 items-center justify-center py-6'>
                    <div className='w-[min(100%,72rem,108svh)] animate-in fade-in slide-in-from-right-10 duration-1000 motion-reduce:animate-none'>
                        <Image
                            src={image.src}
                            width={image.width}
                            height={image.height}
                            alt={t.hero.imageAlt}
                            sizes='(min-width: 1280px) 1152px, 100vw'
                            loading='eager'
                            fetchPriority='high'
                            className='h-auto w-full motion-safe:animate-bike-float'
                        />
                    </div>
                </div>

                <div className='grid gap-6 border-t border-border pt-6 md:grid-cols-[1fr_auto] md:items-end'>
                    <dl className='grid grid-cols-2 gap-x-8 gap-y-5 sm:flex sm:flex-wrap sm:gap-x-12'>
                        <div className='col-span-2 sm:col-span-1'>
                            <dt className='font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground'>
                                {t.hero.priceLabel}
                            </dt>
                            <dd className='mt-1 font-heading text-3xl font-semibold tracking-tight text-foreground'>
                                {PRICE}
                            </dd>
                        </div>

                        <div>
                            <dt className='font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground'>
                                {t.hero.colorLabel}
                            </dt>
                            <dd className='mt-2.5 flex items-center gap-2 text-sm font-medium text-foreground'>
                                <span aria-hidden='true' className='size-3.5 rounded-full bg-gradient-to-br from-race-bright to-race-deep ring-1 ring-white/30' />
                                {t.hero.colorValue}
                            </dd>
                        </div>

                        <div>
                            <dt className='font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground'>
                                {t.hero.sizesLabel}
                            </dt>
                            <dd className='mt-2.5 text-sm font-medium tabular-nums text-foreground'>
                                {FRAME_SIZES.join(' · ')}
                            </dd>
                        </div>
                    </dl>

                    <div className='flex flex-col gap-3 sm:flex-row'>
                        <ButtonLink href='#register' size='lg'>
                            {t.hero.ctaPrimary}
                            <ArrowRight aria-hidden='true' className='size-4' />
                        </ButtonLink>

                        <ButtonLink href='#story' size='lg' variant='outline'>
                            {t.hero.ctaSecondary}
                        </ButtonLink>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default HeroSection
